// Enemy art kit (ENEMY-REFORGE, owner 2026-11-01: 'apply the mock-ups for the looks of the enemies').
// Every enemy family is one merged, vertex-coloured geometry per variant (normal / elite), instanced, and animated on
// the GPU: each vertex carries a pivot and swing/flap/raise weights, and a per-instance vec4 drives the gait.
// This keeps 600–1,000 walking enemies at one draw call per family (+ outline + glow), with no CPU skinning.
// Render-only: the simulation (stats, collision, AI, events) is untouched.
//
// Conventions for family authors:
//   • world units = metres; Aren is ~2.0 m tall. Feet on y = 0, facing +Z, centred on x = z = 0.
//   • parts are added with ArtBuilder.add(geometry, opts). opts.color is the part's colour (vertex colour).
//   • limbs: give the pivot (hip / shoulder / wing root) and `swing` (radians of pitch about local X at full stride;
//     sign gives the direction) plus `sphase` (phase offset: 0 or Math.PI for left/right alternation).
//   • `raise` (radians about X) is applied by the telegraph/wind-up signal (0..1) — e.g. a lifted fist or foot.
//   • `flap` (radians about Z, sign = side) for wings (Rift Moth).
//   • glow parts (eyes, cores, crystal seams) go to the separate unlit glow mesh with `glow: true`.
import * as THREE from 'three/webgpu';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { abs, attribute, color, cos, exp2, float, floor, mod, normalLocal, positionGeometry, positionLocal, sin, step, vec3 } from 'three/tsl';

export interface PartOpts {
  color: number;
  /** pivot (hip/shoulder/wing root) for swing/raise/flap rotation */
  pivot?: [number, number, number];
  /** pitch amplitude (rad) at full stride */
  swing?: number;
  /** gait phase offset (rad) */
  sphase?: number;
  /** extra pitch (rad) applied by the wind-up/telegraph signal */
  raise?: number;
  /** roll amplitude (rad) for wings */
  flap?: number;
  /** goes to the unlit glow mesh */
  glow?: boolean;
}

export interface ArtGeo { body: THREE.BufferGeometry; glow: THREE.BufferGeometry }

/** Collects parts, bakes transforms and animation attributes, returns {body, glow}. */
export class ArtBuilder {
  /** ENEMY-DEATHS (?deaths=new only): tag every vertex with its body-part group (vertex attribute 'part', see PART) */
  static partIds = false;
  private body: THREE.BufferGeometry[] = [];
  private glow: THREE.BufferGeometry[] = [];
  private meta: { g: THREE.BufferGeometry; o: PartOpts; c: THREE.Vector3 }[] = [];
  /** add a part already positioned in model space */
  add(g: THREE.BufferGeometry, o: PartOpts): this {
    const n = g.index ? g.toNonIndexed() : g.clone();
    for (const k of Object.keys(n.attributes)) if (k !== 'position') n.deleteAttribute(k);
    n.computeVertexNormals();
    const cnt = n.attributes.position.count, c = new THREE.Color(o.color);
    const col = new Float32Array(cnt * 3), piv = new Float32Array(cnt * 3), anim = new Float32Array(cnt * 4);
    const p = o.pivot ?? [0, 0, 0];
    for (let i = 0; i < cnt; i++) {
      col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b;
      piv[i * 3] = p[0]; piv[i * 3 + 1] = p[1]; piv[i * 3 + 2] = p[2];
      anim[i * 4] = o.swing ?? 0; anim[i * 4 + 1] = o.sphase ?? 0; anim[i * 4 + 2] = o.raise ?? 0; anim[i * 4 + 3] = o.flap ?? 0;
    }
    n.setAttribute('color', new THREE.BufferAttribute(col, 3));
    n.setAttribute('pivot', new THREE.BufferAttribute(piv, 3));
    n.setAttribute('limb', new THREE.BufferAttribute(anim, 4));
    (o.glow ? this.glow : this.body).push(n);
    if (ArtBuilder.partIds) { n.computeBoundingBox(); this.meta.push({ g: n, o, c: n.boundingBox!.getCenter(new THREE.Vector3()) }); }
    return this;
  }
  /** body-part groups (PART) from each part's role and place: swinging limbs by pivot height and side, wings by side,
   *  the rest by height (head / upper torso / lower torso) or as a back piece (banner, tail) */
  private tagParts(): void {
    let H = 0.1; for (const m of this.meta) H = Math.max(H, m.g.boundingBox!.max.y);
    for (const { g, o, c } of this.meta) {
      const side = c.x < 0 ? 0 : 1, piv = o.pivot ?? [0, 0, 0];
      let id: number;
      if (o.flap) id = PART.armL + side;
      else if (o.swing) id = piv[1] < 0.5 * H ? PART.legL + side : PART.armL + side;
      else if (c.y > 0.8 * H) id = PART.head;
      else if (c.z < -0.22 * H) id = PART.back;
      else id = c.y > 0.5 * H ? PART.upper : PART.lower;
      g.setAttribute('part', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count).fill(id), 1));
    }
  }
  build(): ArtGeo {
    if (ArtBuilder.partIds) this.tagParts();
    const fin = (a: THREE.BufferGeometry[]) => {
      if (!a.length) a = [new ArtBuilder().tiny()];
      const g = mergeGeometries(a, false)!; g.computeBoundingSphere(); return g;
    };
    return { body: fin(this.body), glow: fin(this.glow) };
  }
  private tiny(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0, 0, 0, 0], 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(new Array(9).fill(0), 3));
    g.setAttribute('pivot', new THREE.Float32BufferAttribute(new Array(9).fill(0), 3));
    g.setAttribute('limb', new THREE.Float32BufferAttribute(new Array(12).fill(0), 4));
    if (ArtBuilder.partIds) g.setAttribute('part', new THREE.Float32BufferAttribute([0, 0, 0], 1));
    return g;
  }
}

/** Place a primitive: translate, rotate (Euler XYZ, radians), scale. Returns the same geometry. */
export function at(g: THREE.BufferGeometry, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, s: number | [number, number, number] = 1): THREE.BufferGeometry {
  const m = new THREE.Matrix4();
  const sc = Array.isArray(s) ? new THREE.Vector3(...s) : new THREE.Vector3(s, s, s);
  m.compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), sc);
  return g.applyMatrix4(m);
}
/** A tapered limb segment from point a to point b (radius ra at a, rb at b). */
export function limb(a: [number, number, number], b: [number, number, number], ra: number, rb = ra, seg = 8): THREE.BufferGeometry {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), d = B.clone().sub(A), len = d.length();
  const g = new THREE.CylinderGeometry(rb, ra, len, seg, 1);
  g.translate(0, len / 2, 0);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize()));
  return g.translate(A.x, A.y, A.z);
}
/** Rounded box (bevelled) — armour plates, shields, torsos. */
export function plate(w: number, h: number, d: number, r = 0.04, seg = 2): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d, seg, seg, seg);
  const pos = g.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3();
  const hx = w / 2 - r, hy = h / 2 - r, hz = d / 2 - r;
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const c = new THREE.Vector3(Math.max(-hx, Math.min(hx, v.x)), Math.max(-hy, Math.min(hy, v.y)), Math.max(-hz, Math.min(hz, v.z)));
    const o = v.clone().sub(c); if (o.lengthSq() > 1e-9) o.setLength(r); v.copy(c.add(o));
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  return g;
}

// ---------------------------------------------------------------- GPU animation
/**
 * Per-instance animation vec4: x = gait phase (rad), y = stride amplitude 0..1, z = wind-up/telegraph 0..1,
 * w = hit recoil 0..1; a second per-instance vec4 'anim2' = (cos yaw, sin yaw, uniform scale, 0) carries the instance
 * basis. Returns the animated local position node (used by body, glow and outline materials).
 */
/** body-part groups for blown-apart deaths (vertex attribute 'part') */
export const PART = { head: 0, upper: 1, lower: 2, armL: 3, armR: 4, legL: 5, legR: 6, back: 7 } as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function animatedPosition(parts = false): any {
  // per-instance vec4 lives on each geometry as the instanced attribute 'anim' (so every mesh binds its own buffer)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const A = attribute('anim', 'vec4') as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pivot = attribute('pivot', 'vec3') as any, L = attribute('limb', 'vec4') as any;
  const pitch = sin(A.x.add(L.y)).mul(L.x).mul(A.y).add(L.z.mul(A.z));
  const roll = sin(A.x.mul(2)).mul(0.5).add(0.5).mul(L.w).mul(float(0.35).add(A.y.mul(0.65)));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // three applies the instance matrix to positionLocal BEFORE positionNode runs, so the limb motion is computed in model
  // space (positionGeometry) and only the resulting offset is rotated/scaled into instance space (anim2 = cos yaw, sin yaw, scale)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const G = positionGeometry as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rel = G.sub(pivot);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cp: any = cos(pitch), sp: any = sin(pitch);
  const y1 = rel.y.mul(cp).sub(rel.z.mul(sp)), z1 = rel.y.mul(sp).add(rel.z.mul(cp));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cr: any = cos(roll), sr: any = sin(roll);
  const x2 = rel.x.mul(cr).sub(y1.mul(sr)), y2 = rel.x.mul(sr).add(y1.mul(cr));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p: any = pivot.add(vec3(x2, y2, z1));
  // hit recoil: the whole body rocks back about the feet
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hr = A.w.mul(-0.35), ch: any = cos(hr), sh: any = sin(hr);
  const fin = vec3(p.x, p.y.mul(ch).sub(p.z.mul(sh)), p.y.mul(sh).add(p.z.mul(ch)));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d: any = fin.sub(G), B = attribute('anim2', 'vec4') as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const out = (positionLocal as any).add(vec3(d.x.mul(B.x).add(d.z.mul(B.y)), d.y, d.z.mul(B.x).sub(d.x.mul(B.y))).mul(B.z));
  return parts ? maskParts(out) : out;
}
/** ENEMY-DEATHS: a blown-apart piece (anim4.x = 1) keeps only the part groups set in its bit mask (anim4.y); the other
 *  vertices collapse to one point (degenerate triangles are never rasterised). Apply last, to the final position. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function maskParts(pos: any): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const A4 = attribute('anim4', 'vec4') as any, part = attribute('part', 'float') as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isParts: any = step(abs(A4.x.sub(1)), 0.5), bit: any = mod(floor(A4.y.div(exp2(part))), 2);
  return pos.mul(float(1).sub(isParts.mul(float(1).sub(bit))));
}

/** Outline hull for an animated family: same animation, pushed out along the normal. */
export function animatedOutlineMaterial(thickness: number, ink = 0x0b0612, parts = false, inkMul: ReturnType<typeof float> | null = null): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
  m.colorNode = color(ink);
  const p = animatedPosition().add(normalLocal.mul(inkMul ? float(thickness).mul(inkMul) : float(thickness))); // inkMul: ?look=anime per-class ink
  m.positionNode = parts ? maskParts(p) : p;
  return m;
}
/** Outline geometry that keeps the animation attributes (welded so the hull has no cracks). */
export function animatedOutlineGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = src.clone(); g.deleteAttribute('normal'); for (const k of ['anim', 'anim2']) if (g.getAttribute(k)) g.deleteAttribute(k);
  const w = mergeVertices(g, 1e-4); w.computeVertexNormals(); w.computeBoundingSphere(); return w;
}

// ---------------------------------------------------------------- family spec
export interface EnemyArt {
  /** display name (P1 roster) */
  name: string;
  build(elite: boolean): ArtGeo;
  /** gait: phase (rad) advanced per metre travelled, and idle breathing rate (rad/s) */
  stride: number;
  /** body bob height (m) at full stride, applied on the CPU */
  bob: number;
  /** flying families hover this high (m) above the ground; grounded families use 0 */
  hover?: number;
  /** outline thickness (local units) */
  outline?: number;
}

/** Shared Severed Array palette (P1): ivory armour, dark iron, copper joints, teal crystal; Fractureborn basalt + magenta. */
export const PAL = {
  ivory: 0xe8dcc0, ivoryShade: 0xc9b896, iron: 0x2a2f3c, ironLight: 0x454c5e, navy: 0x1f2a44, copper: 0xc07a3a, copperDark: 0x8a5228,
  teal: 0x3fe0d0, basalt: 0x2b2a30, basaltLight: 0x45434c, magenta: 0xff3ca8, ash: 0x7d7a78, ember: 0xff7a2a, slate: 0x5a6274,
  gold: 0xf0c040, amber: 0xffb030, robe: 0x232a4a, black: 0x15151a, red: 0xe0304a,
};
